import { atom, useAtomValue } from 'jotai';

import { useDeepMemo } from '@crossnokaye/ux-common';

import {
  DisplayRef,
  DisplayRefDisplayType,
  DropdownOption,
  Namespace,
  Ref,
  Schedule,
  Sequence,
  Setting,
  SettingValue,
  Validations,
} from '@repo/sdk/models/components';

import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';

export interface EnhancedSetting {
  ref: Ref;
  description?: string;
  key: string;
  label: string;
  displayUnits?: string;
  units?: string;
  displayType?: DisplayRefDisplayType;
  displayOptions?: DropdownOption[];
  value?: string | number | boolean | Schedule | Sequence | undefined;
  validations?: Validations;
}

/**
 * Hook to get setting values by reactively reading from Jotai atom families.
 * This bypasses the query cache layer and reads directly from the atoms updated by WebSocket handlers.
 *
 * @param settingsRefs - Array of display refs to get values for
 * @returns Array of enhanced settings with current values from Jotai atoms
 */
export const useGetSettingsValues = (settingsRefs: Array<DisplayRef>): Array<EnhancedSetting> => {
  // Create a derived atom that reads all the required setting atoms
  // Use deep memo to prevent recreating the atom when settingsRefs array reference changes
  const derivedAtom = useDeepMemo(() => {
    return atom((get) => {
      if (!settingsRefs || settingsRefs.length === 0) {
        return [];
      }

      return settingsRefs
        .filter((settingRef) => settingRef.ref?.namespace === Namespace.Setting)
        .map((settingRef) => {
          const setting = get(settingsFamily({ settingId: settingRef.ref.id } as Setting));

          const value = setting?.value?.[setting?.kind as keyof SettingValue];

          return {
            description: settingRef.description,
            key: settingRef.ref.id,
            label: settingRef.label,
            ref: settingRef.ref,
            units: settingRef.ref.units,
            displayUnits: settingRef.displayUnits,
            displayType: settingRef.displayType,
            displayOptions: settingRef.displayOptions,
            value,
            validations: settingRef.ref.validations,
          };
        });
    });
  }, [settingsRefs]);

  return useAtomValue(derivedAtom);
};
